import React, { useState } from "react";
import {
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Box,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Divider,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DescriptionIcon from "@material-ui/icons/Description";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import SendIcon from "@material-ui/icons/Send";
import PersonIcon from "@material-ui/icons/Person";
import SearchIcon from "@material-ui/icons/Search";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  docCard: {
    borderRadius: 12,
    height: "100%",
    transition: "box-shadow 0.2s",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    },
  },
  docIcon: {
    fontSize: 40,
    marginBottom: theme.spacing(1),
  },
  checklistItem: {
    borderBottom: "1px solid #f0f0f0",
  },
  statusChip: {
    fontWeight: 600,
  },
}));

const documentTypes = [
  {
    id: "adesao",
    title: "Adesão ao Consórcio",
    icon: "📋",
    color: "#2196f3",
    docs: [
      "RG e CPF (ou CNH)",
      "Comprovante de residência (até 90 dias)",
      "Comprovante de renda (3 últimos meses)",
      "Declaração de IR (se aplicável)",
      "Certidão de estado civil",
    ],
  },
  {
    id: "contemplacao",
    title: "Contemplação",
    icon: "🏆",
    color: "#4caf50",
    docs: [
      "Todos os documentos de adesão",
      "Certidão negativa de débitos",
      "Certidão de matrícula do imóvel (se imóvel)",
      "Laudo de avaliação do bem",
      "Comprovante de quitação de parcelas",
      "Proposta de compra do bem",
    ],
  },
  {
    id: "transferencia",
    title: "Transferência de Cota",
    icon: "🔄",
    color: "#ff9800",
    docs: [
      "Documentos do cedente (vendedor)",
      "Documentos do cessionário (comprador)",
      "Termo de cessão de direitos",
      "Comprovante de pagamento da taxa de transferência",
      "Anuência da administradora",
    ],
  },
  {
    id: "credito",
    title: "Análise de Crédito",
    icon: "🔎",
    color: "#9c27b0",
    docs: [
      "RG e CPF",
      "Comprovante de renda atualizado",
      "Extrato bancário (3 últimos meses)",
      "Declaração de IR completa",
      "Consulta SPC/Serasa (feita pela empresa)",
    ],
  },
  {
    id: "pj",
    title: "Pessoa Jurídica",
    icon: "🏢",
    color: "#607d8b",
    docs: [
      "Contrato Social e alterações",
      "CNPJ atualizado",
      "Balanço patrimonial",
      "DRE dos últimos 3 exercícios",
      "Documentos dos sócios (RG, CPF)",
      "Certidão negativa federal, estadual e municipal",
    ],
  },
  {
    id: "imovel",
    title: "Consórcio Imóvel",
    icon: "🏠",
    color: "#795548",
    docs: [
      "Matrícula atualizada do imóvel",
      "Certidão de ônus reais",
      "IPTU quitado",
      "Laudo de avaliação",
      "Contrato de compra e venda",
      "Certidão negativa de débitos condominiais",
    ],
  },
];

const ConsortiumDocs = () => {
  const classes = useStyles();
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});
  const [search, setSearch] = useState("");
  const [openChecklist, setOpenChecklist] = useState(false);

  const handleToggle = (docId, itemIndex) => {
    const key = `${docId}-${itemIndex}`;
    setCheckedItems({ ...checkedItems, [key]: !checkedItems[key] });
  };

  const getCheckedCount = (docType) => {
    return docType.docs.filter((_, idx) => checkedItems[`${docType.id}-${idx}`]).length;
  };

  const gerarTextoDocumentos = (docType) => {
    const text = `📋 *DOCUMENTOS NECESSÁRIOS*\n` +
      `*${docType.title}*\n\n` +
      docType.docs.map((doc, i) => `${i + 1}. ${doc}`).join("\n") +
      `\n\n⚠️ *Importante:*\n` +
      `• Documentos devem estar legíveis\n` +
      `• Cópias coloridas preferencialmente\n` +
      `• Comprovantes dentro da validade\n\n` +
      `📞 Dúvidas? Estamos à disposição!`;
    return text;
  };

  const handleCopy = (docType) => {
    navigator.clipboard.writeText(gerarTextoDocumentos(docType));
  };

  const filteredDocs = documentTypes.filter(
    (d) => d.title.toLowerCase().includes(search.toLowerCase()) ||
      d.docs.some((doc) => doc.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Documentação de Consórcio
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Checklists de documentos por tipo de operação
          </Typography>
        </div>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Buscar documento..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon style={{ color: "#999", marginRight: 8 }} /> }}
        />
      </div>

      <Grid container spacing={2}>
        {filteredDocs.map((docType) => {
          const checked = getCheckedCount(docType);
          const total = docType.docs.length;
          const complete = checked === total;
          return (
            <Grid item xs={12} sm={6} md={4} key={docType.id}>
              <Card className={classes.docCard}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="h4" style={{ marginBottom: 8 }}>
                      {docType.icon}
                    </Typography>
                    <Chip
                      label={`${checked}/${total}`}
                      size="small"
                      className={classes.statusChip}
                      style={{
                        backgroundColor: complete ? "#e8f5e9" : checked > 0 ? "#fff3e0" : "#f5f5f5",
                        color: complete ? "#2e7d32" : checked > 0 ? "#e65100" : "#666",
                      }}
                      icon={complete ? <CheckCircleIcon style={{ color: "#2e7d32" }} /> : undefined}
                    />
                  </Box>
                  <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 4 }}>
                    {docType.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {total} documentos necessários
                  </Typography>

                  <List dense style={{ marginTop: 8 }}>
                    {docType.docs.slice(0, 3).map((doc, idx) => (
                      <ListItem
                        key={idx}
                        dense
                        button
                        onClick={() => handleToggle(docType.id, idx)}
                        style={{ padding: "2px 0" }}
                      >
                        <ListItemIcon style={{ minWidth: 32 }}>
                          <Checkbox
                            edge="start"
                            checked={!!checkedItems[`${docType.id}-${idx}`]}
                            size="small"
                            style={{ color: docType.color }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={doc}
                          primaryTypographyProps={{
                            variant: "body2",
                            style: {
                              textDecoration: checkedItems[`${docType.id}-${idx}`] ? "line-through" : "none",
                              color: checkedItems[`${docType.id}-${idx}`] ? "#999" : "#333",
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                    {total > 3 && (
                      <Typography variant="caption" color="textSecondary" style={{ paddingLeft: 32 }}>
                        +{total - 3} documentos...
                      </Typography>
                    )}
                  </List>
                </CardContent>
                <CardActions style={{ justifyContent: "space-between", padding: "8px 16px" }}>
                  <Button
                    size="small"
                    onClick={() => { setSelectedDoc(docType); setOpenChecklist(true); }}
                    style={{ color: docType.color }}
                  >
                    Ver Todos
                  </Button>
                  <Tooltip title="Copiar lista para enviar ao cliente">
                    <IconButton size="small" onClick={() => handleCopy(docType)}>
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Modal Checklist Completo */}
      <Dialog open={openChecklist} onClose={() => setOpenChecklist(false)} maxWidth="sm" fullWidth>
        {selectedDoc && (
          <>
            <DialogTitle style={{ backgroundColor: selectedDoc.color, color: "#fff" }}>
              {selectedDoc.icon} {selectedDoc.title}
            </DialogTitle>
            <DialogContent>
              <List>
                {selectedDoc.docs.map((doc, idx) => (
                  <ListItem
                    key={idx}
                    button
                    onClick={() => handleToggle(selectedDoc.id, idx)}
                    className={classes.checklistItem}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={!!checkedItems[`${selectedDoc.id}-${idx}`]}
                        style={{ color: selectedDoc.color }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={doc}
                      primaryTypographyProps={{
                        style: {
                          textDecoration: checkedItems[`${selectedDoc.id}-${idx}`] ? "line-through" : "none",
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleCopy(selectedDoc)} startIcon={<FileCopyIcon />}>
                Copiar Lista
              </Button>
              <Button onClick={() => setOpenChecklist(false)} variant="contained" style={{ backgroundColor: selectedDoc.color, color: "#fff" }}>
                Fechar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default ConsortiumDocs;
